import { useApi } from '@/hooks/useApi'
import { routes } from '@/utilities/routes'
import { useEffect, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { userListSpec, UserRole, type TenantUserRow, type UserRow } from 'aevocam-rest-api-spec'
import { BasicPage } from '@/components/layout/BasicPage'
import { FilterTextBox } from '@/components/scalar/FilterTextBox'
import { Button } from '@/components/action/Button'
import {
  createDataGridState,
  startLoading,
  doneLoading,
  type DataGridState,
} from '@/components/dataGrid/createDataGridState'
import { DataPager } from '@/components/dataGrid/DataPager'
import { toShortDate } from '@/utilities/formatters'
import { DataGrid, type DataGridColumn } from '@/components/dataGrid/DataGrid'
import type { GlobalStateSetter } from '@/utilities/createGlobalState'
import { Tooltip } from '@/components/action/Tooltip'
import './UserList.css'

const PAGE_SIZE = 10

type UserListState = DataGridState<UserRow> & {
  filterText?: string
}

const useUserListState = createDataGridState<UserListState>('userListState', {
  rows: [],
  totalRows: 0,
  skip: 0,
  take: PAGE_SIZE,
})

export function UserList() {

  const api = useApi({ requireLogin: true, adminOnly: true })
  const [pageState, setPageState] = useUserListState()

  useEffect(() => {
    async function fn() {
      startLoading(setPageState, pageState.refreshQuietly)
      try {
        const refreshIncrement = pageState.refresh ?? 0

        const res = await api.request(userListSpec, {
          body: {
            skip: pageState.skip,
            take: pageState.take,
            filterText: pageState.filterText || undefined,
          },
        })
        if (res.status === 200 && res.data?.users) {
          setPageState(prev => {
            if (prev.refresh !== refreshIncrement)
              return prev
            return {
              ...prev,
              rows: res.data?.users?.rows ?? [],
              totalRows: res.data?.users?.totalRows ?? 0,
            }
          })
        }
      } catch (error) {
        console.error(error)
      } finally {
        doneLoading(setPageState)
      }
    }
    void fn()
  }, [
    pageState.skip, pageState.take, pageState.refresh,
    pageState.filterText,
    setPageState,
  ])

  return (
    <BasicPage
      title='Users'
      className='UserList'
      toolbarButtons={<>
        <Button
          label='Admin'
          icon='chevronLeft'
          href={routes.admin()}
        />
      </>}
    >

      <div className='Cutout'>

        <div className='FlexRow ItemsEnd FlexWrap Gap2'>

          <div className='Wrapped FlexGrow'>

            <FilterTextBox
              label='Filter'
              value={pageState.filterText ?? ''}
              setValue={filterText => setPageState(prev => {
                if (prev.filterText === filterText) return prev
                return ({
                  ...prev,
                  filterText,
                  skip: 0,
                })
              })}
            />

          </div>

          <div className='Wrapped'>
            <DataPager
              state={pageState}
              setState={setPageState}
              refreshAfterSeconds={10}
              alignRight
            />
          </div>

        </div>

        <DataGrid
          state={pageState}
          setState={setPageState as GlobalStateSetter<DataGridState<UserRow>>}
          idColumn='id'
          columns={columns}
        />

      </div>

    </BasicPage>
  )
}

const columns: DataGridColumn<UserRow>[] = [
  {
    id: 'name',
    label: 'Name',
    link: (_value, { row }) => routes.adminUserEdit(String(row.id)),
    className: () => 'Ellipsis',
    style: () => ({ maxWidth: '20vw' }),
  },
  {
    id: 'email',
    label: 'Email',
    className: () => 'Ellipsis',
    style: () => ({ maxWidth: '24vw' }),
  },
  {
    id: 'role',
    label: 'Role',
    align: 'center',
    render: value => (value === UserRole.Admin ? 'Admin' : 'User'),
  },
  {
    id: 'disabled',
    label: 'Disabled',
    align: 'center',
    render: value => (value ? 'X' : ''),
  },
  {
    id: 'tenants',
    label: 'Tenants',
    className: () => 'TenantMembershipCell',
    style: () => ({ maxWidth: '20vw' }),
    render: (_value, { row }) => renderUserTenantsCell(row.tenants),
  },
  {
    id: 'createdAt',
    label: 'Created',
    align: 'right',
    render: (value: string | Date | undefined) =>
      value != null ? toShortDate(value) : '',
  },
]

function renderUserTenantsCell(tenants: TenantUserRow[] | undefined): ReactNode {
  if (!tenants?.length) return ''

  const first = tenants[0]!
  const label =
    first.tenantName?.trim()
    || (first.tenantId != null ? `Tenant ${first.tenantId}` : '')

  const name =
    first.tenantId != null && label
      ? (
        <Link to={routes.home(String(first.tenantId))}>
          {label}
        </Link>
      )
      : label

  const extra = tenants.length - 1
  if (extra <= 0) return name

  return (
    <>
      {name}
      {' '}
      <span>
        <Tooltip>
          <ul>
            {tenants.map(t => (
              <li key={t.tenantId}>
                {t.tenantName}
              </li>
            ))}
          </ul>
        </Tooltip>
        (+{extra} more)
      </span>
    </>
  )
}
